import React, { createContext, useContext, useState, ReactNode, useEffect, useRef } from "react";
// import { hubConnection, Proxy } from "signalr-no-jquery";
import {
  ActiveInputType,
  Dates,
  ExtendedFlightSearchResponseItem,
  FlightTravelClass,
  FlightType,
  MobileFilterType,
  SearchState,
  Sequence,
} from "../../site-types";
import { dateToDateStruct, parseLocalDate } from "../../utils/date-utils";
import { concat } from "lodash";
import { graphql, useStaticQuery } from "gatsby";
import { Query, TideAirline, TideAirport, TideItemForDisplayBanner } from "../../types";
import { v4 as uuidv4 } from "uuid";
import { FlightSearchFinishResponse, FlightSearchRequest, FlightSearchResponse } from "@qite/tide-client";
import { useGlobal } from "../global-provider";
import { getDepartureSegment } from "../../utils/flight-utils";

interface SearchContextProps {
  children: ReactNode;
}

// Props wordt gebruikt om de context te bepalen, de velden in de context zetten we in Type
export interface SearchContextType {
  // Qsm State
  from: TideAirport | undefined;
  to: TideAirport | undefined;
  openJawFrom: TideAirport | undefined;
  dates: Dates;
  activeInput: ActiveInputType | null;
  isDatePickerOpen: boolean;
  adults: number;
  kids: number;
  babies: number;
  flightType: FlightType;
  isLuggageIncluded: boolean;
  isOnlyDirect: boolean;
  selectedTravelClass: FlightTravelClass | null;
  selectedAirlines: TideAirline[];
  isOpen: boolean;
  showOverlay: boolean;
  mobileFilterType: MobileFilterType | null;

  // Search States
  isLoadingBarVisible: boolean;
  isDetailLoadingBarVisible: boolean;
  progress: number;
  statusText: string | null;
  searchResults: ExtendedFlightSearchResponseItem[];
  searchDetailsResults: ExtendedFlightSearchResponseItem[];
  displayBanners: TideItemForDisplayBanner[];
  valuesManuallyChanged: boolean;
  isHubReady: boolean;
  currentRequestId: number;

  // Qsm Setters
  setFrom: (val: TideAirport | undefined) => void;
  setTo: (val: TideAirport | undefined) => void;
  setOpenJawFrom: (val: TideAirport | undefined) => void;
  setDates: (dates: Dates) => void;
  setActiveInput: (val: ActiveInputType | null) => void;
  setIsDatePickerOpen: (val: boolean) => void;
  setAdults: (val: number) => void;
  setKids: (val: number) => void;
  setBabies: (val: number) => void;
  setFlightType: (val: FlightType) => void;
  setIsLuggageIncluded: (val: boolean) => void;
  setIsOnlyDirect: (val: boolean) => void;
  setSelectedTravelClass: (val: FlightTravelClass | null) => void;
  setSelectedAirlines: (val: TideAirline[]) => void;
  setIsOpen: (val: boolean) => void;
  setShowOverlay: (val: boolean) => void;
  setMobileFilterType: (val: MobileFilterType | null) => void;

  // Search Setters
  setSearchResults: (results: ExtendedFlightSearchResponseItem[]) => void;
  setValuesManuallyChanged: (val: boolean) => void;
  setCurrentRequestId: (val: number) => void;

  // Methods
  closeMobileFilter: () => void;
  onSearch: (selectedFlight?: ExtendedFlightSearchResponseItem) => void;
  onCancelSearch: () => void;
}

const initialState: SearchContextType = {
  from: undefined,
  to: undefined,
  openJawFrom: undefined,
  dates: { fromDate: undefined, toDate: undefined } as Dates,
  activeInput: null,
  isDatePickerOpen: false,
  adults: 1,
  kids: 0,
  babies: 0,
  flightType: FlightType.Retour,
  isLuggageIncluded: false,
  isOnlyDirect: false,
  selectedTravelClass: null,
  selectedAirlines: [],
  isOpen: false,
  showOverlay: false,
  mobileFilterType: null,
  searchResults: [],
  searchDetailsResults: [],
  isLoadingBarVisible: false,
  isDetailLoadingBarVisible: false,
  progress: 0,
  statusText: "",
  displayBanners: [],
  valuesManuallyChanged: false,
  isHubReady: false,
  currentRequestId: 0,
  setFrom: () => {},
  setTo: () => {},
  setOpenJawFrom: () => {},
  setDates: () => {},
  setActiveInput: () => {},
  setIsDatePickerOpen: () => {},
  setAdults: () => {},
  setKids: () => {},
  setBabies: () => {},
  setFlightType: () => {},
  setIsLuggageIncluded: () => {},
  setIsOnlyDirect: () => {},
  setSelectedTravelClass: () => {},
  setSelectedAirlines: () => {},
  setIsOpen: () => {},
  setShowOverlay: () => {},
  setMobileFilterType: () => {},
  closeMobileFilter: () => {},
  onSearch: () => {},
  onCancelSearch: () => {},
  setSearchResults: () => {},
  setValuesManuallyChanged: () => {},
  setCurrentRequestId: () => {},
};

const SearchContext = createContext<SearchContextType>(initialState);

export const SearchProvider: React.FC<SearchContextProps> = ({ children }) => {
  const { member, airports, airlines } = useGlobal();

  // QSM States
  const [from, setFrom] = useState<TideAirport | undefined>(initialState.from);
  const [to, setTo] = useState<TideAirport | undefined>(initialState.to);
  const [openJawFrom, setOpenJawFrom] = useState<TideAirport | undefined>(initialState.openJawFrom);
  const [dates, setDates] = useState<Dates>(initialState.dates);
  const [activeInput, setActiveInput] = useState<ActiveInputType | null>(initialState.activeInput);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState<boolean>(initialState.isDatePickerOpen);
  const [adults, setAdults] = useState<number>(initialState.adults);
  const [kids, setKids] = useState<number>(initialState.kids);
  const [babies, setBabies] = useState<number>(initialState.babies);
  const [flightType, setFlightType] = useState<FlightType>(initialState.flightType);
  const [isLuggageIncluded, setIsLuggageIncluded] = useState<boolean>(initialState.isLuggageIncluded);
  const [isOnlyDirect, setIsOnlyDirect] = useState<boolean>(initialState.isOnlyDirect);
  const [selectedTravelClass, setSelectedTravelClass] = useState<FlightTravelClass | null>(initialState.selectedTravelClass);
  const [selectedAirlines, setSelectedAirlines] = useState<TideAirline[]>(initialState.selectedAirlines);
  const [isOpen, setIsOpen] = useState<boolean>(initialState.isOpen);
  const [showOverlay, setShowOverlay] = useState<boolean>(initialState.showOverlay);
  const [mobileFilterType, setMobileFilterType] = useState<MobileFilterType | null>(initialState.mobileFilterType);

  // SEARCH States
  const [isLoadingBarVisible, setIsLoadingBarVisible] = useState<boolean>(initialState.isLoadingBarVisible);
  const [progress, setProgress] = useState<number>(initialState.progress);
  const [statusText, setStatusText] = useState<string | null>(initialState.statusText);
  const [searchResults, setSearchResults] = useState<ExtendedFlightSearchResponseItem[]>(initialState.searchResults);
  const [searchDetailsResults, setSearchDetailsResults] = useState<ExtendedFlightSearchResponseItem[]>(initialState.searchDetailsResults);
  const [displayBanners, setDisplayBanners] = useState<TideItemForDisplayBanner[]>(initialState.displayBanners);
  const [valuesManuallyChanged, setValuesManuallyChanged] = useState<boolean>(initialState.valuesManuallyChanged);
  const [isHubReady, setIsHubReady] = useState<boolean>(initialState.isHubReady);
  const [currentRequestId, setCurrentRequestId] = useState<number>(0);

  // internal state
  const [lastSequenceId, setLastSequenceId] = useState<number | null>(null);
  const [sequenceIds, setSequenceIds] = useState<Sequence[]>([]);
  const [regularSearch, setRegularSearch] = useState<SearchState>({
    requestId: null,
    results: [],
    sequenceIds: [],
    lastSequenceId: null,
    loading: true,
  });

  const [detailSearch, setDetailSearch] = useState<SearchState>({
    requestId: null,
    results: [],
    sequenceIds: [],
    lastSequenceId: null,
    loading: true,
  });

  const closeMobileFilter = () => setMobileFilterType(null);

  const hubProxyRef = useRef<any>(null);

  // fetch countries
  const searchQuery = useStaticQuery(graphql`
    query SearchQuery {
      allTideItemForDisplayBanner {
        nodes {
          content {
            general {
              url
              imageDesktop {
                url
                altText
                title
              }
              imageMobile {
                url
                altText
                title
              }
            }
          }
        }
      }
    }
  `) as Query;

  useEffect(() => {
    setDisplayBanners(searchQuery.allTideItemForDisplayBanner.nodes);
  }, [searchQuery]);

  // Connect to SignalR of Tide and set up listeners for results
  useEffect(() => {
    if (typeof window === "undefined") return;
    // @ts-ignore: SignalR augments jQuery
    import("signalr-no-jquery").then(({ hubConnection }) => {
      const connection = hubConnection(process.env.GATSBY_TIDE_HOST, { useDefaultPath: true });
      const hubProxy = connection.createHubProxy("Search");
      let currentId = 0;
      console.log("Connecting to SignalR hub at:", process.env.GATSBY_TIDE_HOST);

      hubProxy.on("FlightResponse", (response: FlightSearchResponse) => {
        setRegularSearch((prev) => {
          if (response.requestId !== prev.requestId) return prev;

          const updated = {
            ...prev,
            results: [...prev.results, ...mapItems(response)],
            sequenceIds: prev.sequenceIds.includes(response.sequenceId) ? prev.sequenceIds : [...prev.sequenceIds, response.sequenceId],
          };

          return {
            ...updated,
            loading: !checkIfFinished(updated),
          };
        });

        setDetailSearch((prev) => {
          if (response.requestId !== prev.requestId) return prev;

          const updated = {
            ...prev,
            results: [...prev.results, ...mapItems(response)],
            sequenceIds: prev.sequenceIds.includes(response.sequenceId) ? prev.sequenceIds : [...prev.sequenceIds, response.sequenceId],
          };
          return {
            ...updated,
            loading: !checkIfFinished(updated),
          };
        });
        console.log("Received FlightResponse:", response.sequenceId);
      });

      hubProxy.on("FinishResponse", (response: FlightSearchFinishResponse) => {
        setRegularSearch((prev) => {
          if (response.requestId !== prev.requestId) return prev;

          const updated = {
            ...prev,
            lastSequenceId: response.sequenceId,
          };

          return {
            ...updated,
            loading: !checkIfFinished(updated),
          };
        });

        setDetailSearch((prev) => {
          if (response.requestId !== prev.requestId) return prev;

          const updated = {
            ...prev,
            lastSequenceId: response.sequenceId,
          };

          return {
            ...updated,
            loading: !checkIfFinished(updated),
          };
        });
      });

      // hubProxy.on("SetRequestId", (requestId: number) => {
      //   setCurrentRequestId(requestId);
      //   currentId = requestId;
      // });

      connection.start().then(() => {
        hubProxyRef.current = hubProxy;
        setTimeout(() => {
          setIsHubReady(true);
        }, 1000); // Delay to ensure hub is ready
      });

      return () => {
        connection.stop();
      };
    });
  }, []);

  // check if all sequences are received
  useEffect(() => {
    if (lastSequenceId != null) {
      let hasAll = true;
      for (var i = 0; i < lastSequenceId + 1; i++) {
        if (!sequenceIds.map((seq) => seq.sequenceId).includes(i)) {
          hasAll = false;
          break;
        }
      }

      if (hasAll) {
        if (isLoadingBarVisible) {
          setIsLoadingBarVisible(false);
        }
      }
    }
  }, [lastSequenceId, sequenceIds]);

  const checkIfFinished = (state: SearchState) => {
    console.log("Checking if finished with state:", state);
    if (state.lastSequenceId === null) return false;

    for (let i = 0; i <= state.lastSequenceId; i++) {
      if (!state.sequenceIds.includes(i)) return false;
    }

    return true;
  };

  const mapItems = (response: FlightSearchResponse) =>
    response.items.map(
      (item) =>
        ({
          ...item,
          guid: uuidv4(),
          requestId: response.requestId,
        } as ExtendedFlightSearchResponseItem)
    );

  const onSearch = async (selectedFlight?: ExtendedFlightSearchResponseItem) => {
    const hubProxy = hubProxyRef.current;
    if (!hubProxy) {
      console.error("Hub proxy not ready yet");
      return;
    }

    const urlParams = new URLSearchParams(location.search);
    const fromDate = urlParams.get("startdate") ? parseLocalDate(urlParams.get("startdate")!) : undefined;
    const toDate = urlParams.get("endDate") ? parseLocalDate(urlParams.get("endDate")!) : undefined;

    const fromIata = urlParams.get("from");
    let departureAirport = airports.find((airport) => airport.iata === fromIata);
    const toIata = urlParams.get("to");
    const destinationAirport = airports.find((airport) => airport.iata === toIata);
    const returnIata = urlParams.get("return");
    const returnAirport = airports.find((airport) => airport.iata === returnIata);
    let onlyDirect = urlParams.get("isOnlyDirect") === "true";
    let luggageIncluded = urlParams.get("luggageIncluded") === "true";
    const travelClassParam = urlParams.get("travelClass");
    const travelClassValue = travelClassParam ? (Number(travelClassParam) as FlightTravelClass) : null;
    const airlinesParam = urlParams.get("airlines");
    const airlineCodes = airlinesParam ? airlinesParam.split(",").filter(Boolean) : [];
    const selectedAirlinesValue = airlineCodes
      .map((code) => airlines.find((airline) => airline.iata === code))
      .filter((airline): airline is TideAirline => Boolean(airline));
    const adults = parseInt(urlParams.get("adults") || "0", 10);
    const kids = parseInt(urlParams.get("kids") || "0", 10);
    const babies = parseInt(urlParams.get("babies") || "0", 10);
    const flightTypeParam = urlParams.get("flightType");
    const flightType = flightTypeParam ? (Number(flightTypeParam) as FlightType) : null;

    setFrom(departureAirport);
    setTo(destinationAirport);
    setOpenJawFrom(returnAirport);
    setDates({ fromDate, toDate } as Dates);
    setSelectedTravelClass(travelClassValue);
    setSelectedAirlines(selectedAirlinesValue);
    setAdults(adults);
    setKids(kids);
    setBabies(babies);
    setIsLuggageIncluded(luggageIncluded);
    setIsOnlyDirect(onlyDirect);
    setFlightType(flightType ?? FlightType.Retour);

    let request = {
      officeId: process.env.OFFICE_ID ? parseInt(process.env.OFFICE_ID) : 24,
      catalogueId: process.env.CATALOGUE_ID ? parseInt(process.env.CATALOGUE_ID) : 9,
      agentId: member?.addressId,
      departureAirportCode: departureAirport?.iata,
      arrivalAirportCode: destinationAirport?.iata,
      returnAirportCode: returnAirport?.iata,
      luggageIncluded: luggageIncluded ? true : null,
      maxStops: onlyDirect ? 0 : null,
      travelClass: travelClassValue,
      pax: concat(
        Array.from({ length: adults }, (_, index) => ({
          id: index,
          age: 31,
        })),
        Array.from({ length: kids }, (_, index) => ({
          id: index + adults,
          age: 8,
        })),
        Array.from({ length: babies }, (_, index) => ({
          id: index + adults + kids,
          age: 1,
        }))
      ),
    } as FlightSearchRequest;

    if (selectedFlight) {
      console.log("Selected flight provided, preparing detailed search request:", selectedFlight);
      request.outward = {
        date: fromDate ? dateToDateStruct(fromDate) : dateToDateStruct(getDepartureSegment(selectedFlight.outward)?.departureDateTime),
        flightCode: selectedFlight.outward.code,
        flightNumbers: selectedFlight.outward.segments.map((s) => s.flightNumber),
        airlines: selectedFlight.outward.segments.map((s) => s.marketingAirlineCode),
      };
      if (flightType !== FlightType.OneWay) {
        request.return = {
          date: toDate ? dateToDateStruct(toDate) : dateToDateStruct(getDepartureSegment(selectedFlight.return)?.departureDateTime),
          flightCode: selectedFlight.return.code,
          flightNumbers: selectedFlight.return.segments.map((s) => s.flightNumber),
          airlines: selectedFlight.return.segments.map((s) => s.marketingAirlineCode),
        };
      }
      request.routeId = selectedFlight.flightRouteId;
      request.flightProvider = selectedFlight.provider;
      request.vendorConfigurationId = selectedFlight.vendorConfigurationId;
      request.maxStops = Math.max(selectedFlight.outward.segments.length, selectedFlight.return?.segments.length ?? 0) - 1;
    } else {
      if (fromDate) {
        request.outward = {
          date: dateToDateStruct(fromDate),
          airlines: airlineCodes.length ? airlineCodes : undefined,
        };
      }

      if (flightType !== FlightType.OneWay && toDate) {
        request.return = {
          date: dateToDateStruct(toDate),
          // airlines: airlineCodes.length ? airlineCodes : undefined,
        };
      }
    }

    try {
      if (selectedFlight) {
        if (detailSearch.requestId) {
          hubProxy.invoke("CancelSearch", detailSearch.requestId);
        }
        const requestId = await hubProxy.invoke("SearchFlights", request);
        console.log(`Invoking detailed search with for id ${requestId} with request:`, request);

        setDetailSearch({
          requestId,
          results: [],
          sequenceIds: [],
          lastSequenceId: null,
          loading: true,
        });
      } else {
        if (regularSearch.requestId) {
          hubProxy.invoke("CancelSearch", regularSearch.requestId);
        }
        const requestId = await hubProxy.invoke("SearchFlights", request);
        console.log(`Invoking regular search with for id ${requestId} with request:`, request);
        setCurrentRequestId(requestId);
        setRegularSearch({
          requestId,
          results: [],
          sequenceIds: [],
          lastSequenceId: null,
          loading: true,
        });
      }
    } catch (err) {
      console.error("Error invoking search:", err);
    }
  };

  const onCancelSearch = () => {
    const hubProxy = hubProxyRef.current;
    if (!hubProxy) {
      console.error("Hub proxy not ready yet");
      return;
    }

    try {
      if (currentRequestId) {
        hubProxy.invoke("CancelSearch", currentRequestId);
      }
      console.log("CancelSearch invoked");
    } catch (err) {
      console.error("Error invoking search:", err);
    }
  };

  return (
    <SearchContext.Provider
      value={{
        from,
        to,
        openJawFrom,
        dates,
        activeInput,
        isDatePickerOpen,
        adults,
        kids,
        babies,
        flightType,
        isLuggageIncluded,
        isOnlyDirect,
        selectedTravelClass,
        selectedAirlines,
        isOpen,
        showOverlay,
        mobileFilterType,
        currentRequestId,

        isLoadingBarVisible: regularSearch.loading,
        isDetailLoadingBarVisible: detailSearch.loading,
        progress,
        statusText,
        searchResults: regularSearch.results,
        searchDetailsResults: detailSearch.results,
        displayBanners,
        valuesManuallyChanged,
        isHubReady,

        setFrom,
        setTo,
        setOpenJawFrom,
        setDates,
        setActiveInput,
        setIsDatePickerOpen,
        setAdults,
        setKids,
        setBabies,
        setFlightType,
        setIsLuggageIncluded,
        setIsOnlyDirect,
        setSelectedTravelClass,
        setSelectedAirlines,
        setIsOpen,
        setShowOverlay,
        setMobileFilterType,

        setSearchResults,
        setValuesManuallyChanged,
        setCurrentRequestId,

        closeMobileFilter,
        onSearch,
        onCancelSearch,
      }}
    >
      {children}
    </SearchContext.Provider>
  );
};

export default SearchContext;

export const useSearch = (): SearchContextType => {
  const context = useContext(SearchContext);
  if (!context) throw new Error("useQsm must be used within a QsmProvider");
  return context;
};
